"use client";

import { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useRouter } from "next/navigation";
import { signOut } from "firebase/auth";
import { auth } from "@/lib/firebase";
import {
  FaUser,
  FaSignOutAlt,
  FaStore,
  FaUtensils,
  FaShoppingBag,
} from "react-icons/fa";

const DropdownContainer = styled.div`
  position: relative;
`;

const ProfileButton = styled.button`
  padding: 0.5rem 1rem;
  background: #c8553d;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: background 0.3s;

  &:hover {
    background: #b04731;
  }
`;

const ProfileIcon = styled.span`
  font-size: 1.2rem;
`;

const DropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 0.5rem);
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? "visible" : "hidden")};
  transform: ${(props) =>
    props.$isOpen ? "translateY(0)" : "translateY(-10px)"};
  transition: all 0.3s ease;
  z-index: 1000;
  overflow: hidden;
`;

const MenuItem = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-size: 0.95rem;
  color: #333;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: background 0.2s;

  &:hover {
    background: #f5f5f5;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f0f0f0;
  }
`;

const MenuIcon = styled.span`
  font-size: 1.1rem;
  width: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoutItem = styled(MenuItem)`
  color: #c8553d;
  font-weight: 600;

  &:hover {
    background: #fff3f3;
  }
`;

interface ProfileDropdownProps {
  isArtisan?: boolean;
}

export default function ProfileDropdown({
  isArtisan = false,
}: ProfileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      router.push("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleProfile = () => {
    setIsOpen(false);
    router.push("/profile");
  };

  const handleArtisanDashboard = () => {
    setIsOpen(false);
    router.push("/artisan-dashboard");
  };

  const handleBecomeArtisan = () => {
    setIsOpen(false);
    router.push("/onboarding/artisan-benefits");
  };

  const handleMyOrders = () => {
    setIsOpen(false);
    router.push("/orders");
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <ProfileButton onClick={toggleDropdown}>
        <ProfileIcon>
          <FaUser />
        </ProfileIcon>
        Profile
      </ProfileButton>

      <DropdownMenu $isOpen={isOpen}>
        {isArtisan && (
          <MenuItem onClick={handleArtisanDashboard}>
            <MenuIcon>
              <FaStore />
            </MenuIcon>
            Artisan Dashboard
          </MenuItem>
        )}
        {!isArtisan && (
          <>
            <MenuItem onClick={handleBecomeArtisan}>
              <MenuIcon>
                <FaUtensils />
              </MenuIcon>
              Become an Artisan
            </MenuItem>
            <MenuItem onClick={handleMyOrders}>
              <MenuIcon>
                <FaShoppingBag />
              </MenuIcon>
              My Orders
            </MenuItem>
          </>
        )}
        <MenuItem onClick={handleProfile}>
          <MenuIcon>
            <FaUser />
          </MenuIcon>
          My Profile
        </MenuItem>
        <LogoutItem onClick={handleLogout}>
          <MenuIcon>
            <FaSignOutAlt />
          </MenuIcon>
          Logout
        </LogoutItem>
      </DropdownMenu>
    </DropdownContainer>
  );
}
